import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
  Alert,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Refresh,
  Receipt,
  Payment,
  Visibility
} from '@mui/icons-material';
import { getTransactions } from '../../Utils/apiServices';
import './Transactions.css';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTransactions();
      if (response.data.success) {
        setTransactions(response.data.data || []);
      } else {
        setError('Failed to fetch transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Error fetching transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'warning';
      case 'COMPLETED':
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'CANCELLED':
        return 'default';
      default:
        return 'info';
    }
  };

  const formatAmount = (amount) => {
    if (amount == null || Number.isNaN(Number(amount))) return 'N/A';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTransactions = statusFilter === 'ALL'
    ? transactions
    : transactions.filter(t => t.status?.toUpperCase() === statusFilter);

  const countByStatus = (s) => transactions.filter(t => t.status?.toUpperCase() === s).length;

  const getTransactionIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return <Payment color="warning" />;
      case 'COMPLETED':
      case 'SUCCESS':
        return <Receipt color="success" />;
      case 'FAILED':
        return <Payment color="error" />;
      default:
        return <Receipt color="info" />;
    }
  };



  return (
    <Box className="transactions-container">
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ef4444 0%, #f97316 50%, #eab308 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(239,68,68,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>💳 Transactions</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Complete payment history and transaction records</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{transactions.length}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Records</Typography>
        </Box>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }} className="transactions-card">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Transaction History</Typography>
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(_, val) => { if (val !== null) setStatusFilter(val); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    px: 2,
                    borderRadius: '20px !important',
                    border: '1px solid',
                    borderColor: 'divider',
                    mx: 0.5,
                    '&.Mui-selected': { color: '#fff' },
                  },
                  '& .MuiToggleButtonGroup-grouped': {
                    borderRadius: '20px !important',
                    border: '1px solid !important',
                    borderColor: 'divider !important',
                  },
                }}
              >
                <ToggleButton
                  value="ALL"
                  sx={{ '&.Mui-selected': { bgcolor: '#6366f1', borderColor: '#6366f1 !important', '&:hover': { bgcolor: '#4f46e5' } } }}
                >
                  All&nbsp;<Chip label={transactions.length} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: 'rgba(0,0,0,0.08)', pointerEvents: 'none' }} />
                </ToggleButton>
                <ToggleButton
                  value="SUCCESS"
                  sx={{ '&.Mui-selected': { bgcolor: '#10b981', borderColor: '#10b981 !important', '&:hover': { bgcolor: '#059669' } } }}
                >
                  Success&nbsp;<Chip label={countByStatus('SUCCESS')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: 'rgba(0,0,0,0.08)', pointerEvents: 'none' }} />
                </ToggleButton>
                <ToggleButton
                  value="PENDING"
                  sx={{ '&.Mui-selected': { bgcolor: '#f59e0b', borderColor: '#f59e0b !important', '&:hover': { bgcolor: '#d97706' } } }}
                >
                  Pending&nbsp;<Chip label={countByStatus('PENDING')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: 'rgba(0,0,0,0.08)', pointerEvents: 'none' }} />
                </ToggleButton>
                <ToggleButton
                  value="FAILED"
                  sx={{ '&.Mui-selected': { bgcolor: '#ef4444', borderColor: '#ef4444 !important', '&:hover': { bgcolor: '#dc2626' } } }}
                >
                  Failed&nbsp;<Chip label={countByStatus('FAILED')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: 'rgba(0,0,0,0.08)', pointerEvents: 'none' }} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Button
              variant="contained"
              onClick={fetchTransactions}
              startIcon={<Refresh />}
              disabled={loading}
              sx={{ flexShrink: 0 }}
            >
              Refresh
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Showing {filteredTransactions.length} of {transactions.length} transactions
          </Typography>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Transactions Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No {statusFilter !== 'ALL' ? statusFilter.toLowerCase() : ''} transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((transaction, index) => (
                    <TableRow key={transaction.id || index}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Chip 
                            label={transaction.transaction_number} 
                            color="primary" 
                            size="small"
                            sx={{ mr: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={String(transaction.user_id ?? '—')}
                          color="secondary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatAmount(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {transaction.purpose ? String(transaction.purpose) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Tooltip title={transaction.status}>
                            <IconButton size="small" sx={{ mr: 1 }}>
                              {getTransactionIcon(transaction.status)}
                            </IconButton>
                          </Tooltip>
                          <Chip 
                            label={transaction.status} 
                            color={getStatusColor(transaction.status)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(transaction.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" color="primary">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Transactions; 